/**
 * What each workspace is called, in the chooser and mid-sentence, and the
 * few sentences the account screen says before a workspace is open.
 *
 * Both label maps are keyed by `RoleKey` so a role added to the shared
 * package is a compile error here rather than a blank in the chooser. A
 * portal's own copy — its eyebrow, its rail, its headers — lives on its
 * spec, not here.
 */

import type { MembershipLevel, RoleKey } from "@ondo/shared";

/** Human label for each workspace, shown in the chooser and the header. */
export const WORKSPACE_LABELS: Record<RoleKey, string> = {
  collector: "Collector",
  estate: "Artist / Estate",
  agent: "Agent",
  gallery: "Gallery",
  verifier: "Verification",
};

/** Lower-case form for mid-sentence use: "isn't registered as an agent". */
export const WORKSPACE_NOUNS: Record<RoleKey, string> = {
  collector: "a collector",
  estate: "an artist or estate",
  agent: "an agent",
  gallery: "a gallery",
  verifier: "a verifier",
};

export const ACCESS_REQUEST_URL = import.meta.env.ACCESS_REQUEST_URL;

export const WORKSPACE_COPY = {
  loading: "Loading your account…",
  unreachableTitle: "Can’t reach ONDO",
  errorTitle: "Something went wrong",
  unprovisionedTitle: "Account not linked yet",
  unprovisioned:
    "You are signed in, but this account has no ONDO record. An administrator needs to link it before you can go further.",
  chooseTitle: "Choose a workspace",
  yourWorkspaces: "Your workspaces",
  noEntities: "You’re not part of any entity on ONDO yet.",
} as const;

/**
 * The empty chooser's door — OND-12.
 *
 * An invited account arrives having declared nothing, and the chooser used to
 * end at "apply for access". The route to declare a capacity already existed
 * (`PUT /v1/me/declared-types`); these are the words around it.
 *
 * None of these sentences may say a role was GRANTED. Declaring opens a
 * workspace over the account's own principal and nothing else —
 * `DeclareTypesRequest` in @ondo/shared is the argument — so the copy says
 * "opens" and "your own", never "you now have access".
 */
export const DECLARE_COPY = {
  legend: "What do you do?",
  hint: "Tick everything that applies. Each one opens a workspace shaped for it, over your own account and nothing else.",
  submit: "Open my workspace",
  submitting: "Opening…",
  nothingChosen: "Tick at least one.",
  failed: "Could not record that. Try again.",
  /** The account may still be a member of a firm; that door is the firm's, not this form's. */
  belongToAFirm:
    "If you work for a gallery, an estate or an advisory, ask it to add you as a member instead — that workspace is the firm's to open.",
} as const;

/**
 * Why the chooser is empty, in one sentence each. `{name}` is the principal.
 *
 * The two states the ticket insists on telling apart: holding NOTHING, and
 * holding a collection somebody shared that may only be read. A person in the
 * second has something on ONDO already and must not be told to create what they
 * appear to have — so that sentence names the shared collection and says what
 * declaring will do to it (nothing).
 */
export const EMPTY_CHOOSER_COPY = {
  nothing: "{name} is registered, but holds no collection, membership or mandate to open yet.",
  sharedReadOnly:
    "{name} can read {count} shared with them, and holds nothing of their own to open a workspace on. Declaring what you do opens one; the shared collection stays exactly as it is, and appears inside the collector workspace.",
  shared:
    "{name} has access to {count} somebody else owns, and holds nothing of their own to open a workspace on. Declaring what you do opens one; those collections are unaffected.",
  /** The collections could not be read, so neither sentence above can be claimed. */
  unknown: "{name} is registered, but has no workspace to open yet.",
} as const;

/**
 * The claim-your-studio banner — OND-7, over OND-6's door.
 *
 * `{address}` is the sign-in address the studio was invited at and `{waiting}`
 * is `plural()`'s output. The copy says the person is TAKING THE STUDIO'S SEAT
 * and may rename it, and never that a role was granted: the capacity the
 * server derives afterwards is the firm's, not a permission handed to them.
 */
export const CLAIM_STUDIO_COPY = {
  eyebrow: "A studio is waiting for you",
  sentence:
    "Somebody asked a studio at {address} to confirm authorship, and {waiting} for whoever takes its seat. If that studio is you — the artist, or the estate — take the seat and answer as it. You can rename it afterwards.",
  waiting: ["request is waiting", "requests are waiting"] as const,
  claim: "Act as this studio",
  claiming: "Taking the seat…",
  failed: "Could not take the seat. Try again.",
  /** Where the claim lands when the new profile names the studio's workspace. */
  landing: "verifications",
} as const;

/**
 * How the three rungs read to somebody deciding whether to accept — OND-24.
 *
 * NAMED RATHER THAN SPELLED, for the reason the invitation email names them the
 * same way: `admin` is a term of art, and the rung is the whole content of the
 * offer. The rung is frozen on the row, so accepting gives exactly what this
 * describes — `20261014092200` decision 4 — and these are descriptions of a fact
 * rather than approximations of one.
 */
export const SEAT_INVITATION_RUNGS: Record<MembershipLevel, string> = {
  admin: "an administrator, which can add and remove colleagues",
  editor: "an editor, which can record works, sales and loans",
  viewer: "a viewer, which can read the firm's records",
};

/**
 * The seat-invitation banner — OND-24, and `CLAIM_STUDIO_COPY`'s shape one
 * ticket along.
 *
 * `{firm}`, `{rung}`, `{inviter}` and `{address}` are filled in by
 * `describeSeatInvitations`. The copy says this is a SEAT AT A FIRM and never
 * that an account was created or a role granted: nothing was created for the
 * invited address, which is the sentence an invited colleague most needs —
 * `20261014092200` decision 5.
 *
 * IT NAMES THE ADDRESS, because that is how somebody tells an invitation meant
 * for them from one sent to a shared mailbox they happen to read — and the
 * address is their own, matched against their session before the row was
 * selected.
 */
export const SEAT_INVITATION_COPY = {
  eyebrow: "You have been invited to a firm",
  sentence:
    "{inviter} has invited {address} to a seat at {firm}, as {rung}. Accepting seats you there under your own account; nothing at the firm is open to you until you do, and declining creates nothing either way.",
  accept: "Accept the seat",
  accepting: "Taking the seat…",
  decline: "Decline",
  declining: "Declining…",
  acceptFailed: "Could not take the seat. Try again.",
  declineFailed: "Could not decline. Try again.",
  /** Where an acceptance lands when the new profile names the firm's workspace. */
  landing: "dashboard",
} as const;
